import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useStores } from "@/stores";

interface CreateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SCOPE_OPTIONS: { value: string; label: string; hint: string }[] = [
  { value: "org", label: "Org", hint: "Granted at the organisation level" },
  { value: "building", label: "Building", hint: "Granted per building" },
  { value: "project", label: "Project", hint: "Granted per project" },
];

const CODE_PATTERN = /^[a-z0-9_]+$/;

export function CreateRoleDialog({ open, onOpenChange }: CreateRoleDialogProps) {
  const { rolesStore } = useStores();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState<string>("org");
  const [deprecated, setDeprecated] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const reset = () => {
    setCode("");
    setName("");
    setDescription("");
    setScope("org");
    setDeprecated(false);
  };

  const handleCreate = async () => {
    const trimmedCode = code.trim();
    const trimmedName = name.trim();

    if (!trimmedCode) {
      toast.error("Role code is required");
      return;
    }
    if (!CODE_PATTERN.test(trimmedCode)) {
      toast.error("Code must contain only lowercase letters, numbers, and underscores");
      return;
    }
    if (trimmedCode.length > 50) {
      toast.error("Code must be 50 characters or fewer");
      return;
    }
    if (!trimmedName) {
      toast.error("Role name is required");
      return;
    }
    if (rolesStore.getByCode(trimmedCode)) {
      toast.error(`A role with code "${trimmedCode}" already exists`);
      return;
    }

    setIsCreating(true);
    try {
      await rolesStore.createRole({
        code: trimmedCode,
        name: trimmedName,
        description: description.trim() || null,
        scope,
        deprecated,
      });
      toast.success(`Role "${trimmedName}" created`);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create role");
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !isCreating) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Create New Role</DialogTitle>
          <DialogDescription>
            Roles are granted to users via <code>org_user_roles</code>. Each role bundles a set of
            permissions which you can edit after creating it.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="role_code">
              Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="role_code"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="e.g. site_inspector"
              disabled={isCreating}
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, and underscores only. Cannot be changed later.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="role_name">
              Display name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="role_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Site Inspector"
              disabled={isCreating}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="role_scope">Scope</Label>
            <Select value={scope} onValueChange={setScope} disabled={isCreating}>
              <SelectTrigger id="role_scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex flex-col">
                      <span>{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.hint}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="role_description">Description</Label>
            <Textarea
              id="role_description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who is this role for, and what does it let them do?"
              className="min-h-[80px]"
              disabled={isCreating}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={deprecated}
              onCheckedChange={(c) => setDeprecated(c === true)}
              disabled={isCreating}
            />
            <span>Mark as deprecated (hide from new role assignments)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              "Create role"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
